import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { socketService } from "../../../services/socket";
import { slugify } from "../../../utils/slugify";
import { useAuth } from "../../auth/contexts/AuthContext";

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: number;
  // Project framerate used by timecoded exports (AVID locators, FCPXML). Optional
  // because older rows may predate the column; backend falls back to 25 when absent.
  framerate?: number;
}

export interface CreationProgress {
  status: "idle" | "creating" | "creating-ai" | "ready" | "error";
  message: string;
}

interface ProjectContextType {
  projects: Project[];
  activeProject: Project | null;
  participantList: any[];
  selectProject: (projectId: string) => void;
  findProjectBySlug: (slug: string) => Project | undefined;
  createProject: (name: string, description: string) => void;
  deleteProject: (projectId: string) => void;
  clearActiveProject: () => void;
  isLoading: boolean;
  creationProgress: CreationProgress;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const [projects, setProjects] = useState<Project[]>([]);
  const [participantList, setParticipantList] = useState<any[]>([]);
  // Active project is determined solely by the URL slug (via ProjectSlugGuard).
  // No localStorage persistence — avoids stale project IDs across sessions.
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const activeProjectIdRef = useRef<string | null>(null);

  // Keep ref in sync so reconnect handler always has the latest value
  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);
  const [isLoading, setIsLoading] = useState(true);
  const [creationProgress, setCreationProgress] = useState<CreationProgress>({
    status: "idle",
    message: "",
  });

  // Release the loading lock immediately if the user is not authenticated,
  // since the socket will never connect to fetch projects.
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      setIsLoading(false);
    }
  }, [isAuthLoading, isAuthenticated]);

  useEffect(() => {
    const handleProjectsUpdate = (updatedProjects: Project[]) => {
      setProjects(updatedProjects);
      setIsLoading(false);
    };

    const handleCreationProgress = (progress: CreationProgress) => {
      setCreationProgress(progress);
      // Auto-reset after 3 seconds on success or error
      if (progress.status === "ready" || progress.status === "error") {
        setTimeout(() => setCreationProgress({ status: "idle", message: "" }), 3000);
      }
    };

    const handleParticipantsUpdate = (updatedParticipants: any[]) => {
      setParticipantList(updatedParticipants || []);
    };

    const fetchProjects = () => {
      socketService.emit("get-projects");
    };

    socketService.on("projects-list-updated", handleProjectsUpdate);
    socketService.on("participants-updated", handleParticipantsUpdate);
    socketService.on("project-creation-progress", handleCreationProgress);
    const reselectSavedProject = () => {
      // Re-select in-memory project on reconnect (no localStorage)
      const currentId = activeProjectIdRef.current;
      if (currentId) {
        socketService.selectProject(currentId);
        socketService.emit("get-participants", (data: any[]) => {
          setParticipantList(data || []);
        });
      }
    };

    const handleConnect = () => {
      fetchProjects();
      reselectSavedProject();
    };

    socketService.on("connect", handleConnect);

    // Initial fetch only if already connected
    // The 'connect' listener will handle it if not yet connected
    if (socketService.connected) {
      handleConnect();
    }

    // Safety timeout: stop loading after 10s (or longer in E2E) even if no
    // projects found. The auth blocking path (setTimeout de 1s en AuthContext +
    // verify-profile HTTP + socket connect) puede superar 2s en E2E; 10s evita
    // que ProjectSlugGuard redirija a /projects con projects=[].
    // In E2E, window.__PROJECT_LOAD_TIMEOUT_MS__ can extend this to allow for
    // slow Supabase queries under parallel load (e.g. 60000 ms).
    const safetyMs =
      typeof window !== "undefined" && (window as any).__PROJECT_LOAD_TIMEOUT_MS__
        ? (window as any).__PROJECT_LOAD_TIMEOUT_MS__
        : 10000;
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, safetyMs);

    return () => {
      socketService.off("projects-list-updated", handleProjectsUpdate);
      socketService.off("participants-updated", handleParticipantsUpdate);
      socketService.off("project-creation-progress", handleCreationProgress);
      socketService.off("connect", handleConnect);
      clearTimeout(timeoutId);
    };
  }, []);

  const activeProject = projects.find((p) => p.id === activeProjectId) || null;

  const selectProject = useCallback((projectId: string) => {
    // Skip if already selected — avoids duplicate select-project → init-data
    if (activeProjectIdRef.current === projectId) return;
    setActiveProjectId(projectId);
    socketService.selectProject(projectId);
    socketService.emit("get-participants", (data: any[]) => {
      setParticipantList(data || []);
    });
  }, []);

  const createProject = (name: string, description: string) => {
    setCreationProgress({
      status: "creating",
      message: t("common.context.creatingProject"),
    });
    socketService.emit("create-project", { name, description });
  };

  const deleteProject = (projectId: string) => {
    socketService.emit("delete-project", projectId);
  };

  const findProjectBySlug = (slug: string): Project | undefined => {
    return projects.find((p) => slugify(p.name) === slug);
  };

  const clearActiveProject = () => {
    setActiveProjectId(null);
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProject,
        participantList,
        selectProject,
        findProjectBySlug,
        createProject,
        deleteProject,
        clearActiveProject,
        isLoading,
        creationProgress,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error("useProjects must be used within a ProjectProvider");
  }
  return context;
};
